<template>
  <div>
    <PTransparentLoader :loading="loading">
      <CRow>
        <CCol lg="12">
          <div class="d-flex align-items-center justify-content-between mb-4">
            <h1 class="main-header">
              <translate translate-context="payout">Payout #{{ payoutId }}</translate>
            </h1>
            <CButton
              color="secondary"
              variant="outline"
              @click="$router.push({ name: 'payoutList', params: { contractId: contractId } })"
            >
              <CIcon name="cipArrowLeft" size="sm" class="mr-2"/>
              <translate translate-context="payout">Back to list</translate>
            </CButton>
          </div>
          
          <!-- Alert for requested status without invoice -->
          <CAlert 
            v-if="payout && payout.status === 'requested' && !payout.invoice"
            color="warning" 
            class="mb-4"
          >
            <div class="d-flex align-items-start">
              <CIcon name="cipWarning" size="lg" class="mr-3 mt-1"/>
              <div>
                <h6 class="alert-heading mb-2">
                  <translate translate-context="payout">Invoice Required</translate>
                </h6>
                <p class="mb-2">
                  <translate translate-context="payout">This payout request is waiting for invoice upload. Please upload your invoice to complete the payment request.</translate>
                </p>
                <CButton
                  color="warning"
                  size="sm"
                  @click="openPayoutModal"
                >
                  <CIcon name="cipUpload" size="sm" class="mr-1"/>
                  <translate translate-context="payout">Upload Invoice Now</translate>
                </CButton>
              </div>
            </div>
          </CAlert>
          
          <!-- Payout Info -->
          <CCard class="mb-4" v-if="payout">
            <CCardHeader>
              <h5 class="mb-0" v-translate translate-context="payout">Payout Information</h5>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol lg="3">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">Status</label>
                    <div>
                      <CBadge :color="getStatusColor(payout.status)" class="badge-outline">
                        <div class="d-flex align-items-center">
                          <CIcon :name="getStatusIcon(payout.status)" size="custom-size" :height="8" class="mr-2"/>
                          <span class="font-weight-normal">{{ getStatusLabel(payout.status) }}</span>
                        </div>
                      </CBadge>
                    </div>
                  </div>
                </CCol>
                <CCol lg="3">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">Net Amount</label>
                    <div class="h4 mb-0 text-primary">{{ payout.paid_net | currency(contract ? contract.currency : 'EUR') }}</div>
                    <div class="text-muted small">
                      <translate translate-context="payout">Gross</translate>: {{ payout.paid_gross | currency(contract ? contract.currency : 'EUR') }}
                    </div>
                    <div class="text-muted small">
                      <translate translate-context="payout">Fees</translate>: {{ payout.pica_fees | currency(contract ? contract.currency : 'EUR') }}
                    </div>
                  </div>
                </CCol>
                <CCol lg="3">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">Period</label>
                    <div>{{ payout.date_from | date }} - {{ payout.date_to | date }}</div>
                  </div>
                </CCol>
                <CCol lg="3">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">Statistics</label>
                    <div>
                      <translate translate-context="payout">Orders</translate>: {{ payout.num_orders_total | number }}
                    </div>
                    <div>
                      <translate translate-context="payout">Photos</translate>: {{ payout.photos_sold | number }}
                    </div>
                    <div>
                      <translate translate-context="payout">Events</translate>: {{ payout.events_count | number }}
                    </div>
                  </div>
                </CCol>
              </CRow>
              
              <CRow class="mt-3">
                <CCol lg="4">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">IBAN</label>
                    <div>{{ payout.iban || '-' }}</div>
                  </div>
                </CCol>
                <CCol lg="4">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">Invoice</label>
                    <div v-if="payout.invoice_number">
                      {{ payout.invoice_number }}
                    </div>
                    <div v-else-if="payout.status === 'requested' && !payout.invoice">
                      <CAlert color="warning" class="py-2 px-3 mb-0">
                        <div class="d-flex align-items-center">
                          <CIcon name="cipWarning" size="sm" class="mr-2"/>
                          <translate translate-context="payout">Invoice upload pending</translate>
                        </div>
                      </CAlert>
                    </div>
                    <div v-else class="text-muted">-</div>
                  </div>
                </CCol>
                <CCol lg="4">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">Payment Date</label>
                    <div>{{ payout.paid_at ? (payout.paid_at | dateTime) : '-' }}</div>
                  </div>
                </CCol>
              </CRow>
              
              <CRow v-if="payout.notes" class="mt-3">
                <CCol lg="12">
                  <div class="mb-3">
                    <label class="text-muted small text-uppercase" v-translate translate-context="payout">Notes</label>
                    <div class="p-3 bg-light rounded">{{ payout.notes }}</div>
                  </div>
                </CCol>
              </CRow>
              
              <!-- Downloads -->
              <CRow class="mt-4">
                <CCol lg="12">
                  <label class="text-muted small text-uppercase mb-2" v-translate translate-context="payout">Downloads</label>
                  <div class="d-flex gap-3">
                    <CButton
                      v-if="payout.invoice"
                      color="primary"
                      variant="outline"
                      :href="payout.invoice"
                      target="_blank"
                    >
                      <CIcon name="cipFile03" size="sm" class="mr-2"/>
                      <translate translate-context="payout">Download Invoice</translate>
                    </CButton>
                    <CButton
                      v-else-if="payout.status === 'requested' && !payout.invoice"
                      color="warning"
                      @click="openPayoutModal"
                    >
                      <CIcon name="cipUpload" size="sm" class="mr-2"/>
                      <translate translate-context="payout">Upload Invoice</translate>
                    </CButton>
                    <CButton
                      v-if="payout.consolidated_csv || payout.id"
                      color="primary"
                      variant="outline"
                      @click="handleDownloadConsolidatedCsv"
                    >
                      <CIcon name="cipDownload" size="sm" class="mr-2"/>
                      <translate translate-context="payout">Download Consolidated Excel</translate>
                    </CButton>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
          
          <!-- PicaService Details -->
          <CCard v-if="payout && payout.pica_services && payout.pica_services.length > 0">
            <CCardHeader>
              <h5 class="mb-0" v-translate translate-context="payout">Service Details</h5>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                :items="payout.pica_services"
                :fields="serviceFields"
                :sorter="false"
                striped
                hover
              >
                <template #pica_service_name="{ item }">
                  <td>
                    <div>{{ item.pica_service_name }}</div>
                    <div v-if="item.date" class="text-muted small">
                      <CIcon name="cipCalendar" size="sm" class="mr-1"/>
                      {{ item.date | date }}
                    </div>
                  </td>
                </template>
                <template #photos_sold="{ item }">
                  <td class="text-center">{{ item.photos_sold | number }}</td>
                </template>
                <template #paid_net="{ item }">
                  <td class="text-right">
                    <div class="font-weight-bold">
                      {{ item.paid_net | currency(contract ? contract.currency : 'EUR') }}
                    </div>
                    <div class="text-muted small">
                      <translate translate-context="payout">Gross</translate>: {{ item.paid_gross | currency(contract ? contract.currency : 'EUR') }}
                    </div>
                    <div class="text-muted small">
                      <translate translate-context="payout">Fees</translate>: {{ item.pica_fees | currency(contract ? contract.currency : 'EUR') }}
                    </div>
                  </td>
                </template>
                <template #last_order_paid_at="{ item }">
                  <td class="text-center">
                    <span v-if="item.last_order_paid_at">
                      {{ item.last_order_paid_at | dateTime }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>
                <template #csv_file="{ item }">
                  <td class="text-center">
                    <CButton
                      v-if="item.csv_file || item.pica_service_id"
                      color="primary"
                      size="sm"
                      variant="ghost"
                      @click="handleDownloadServiceCsv(item.pica_service_id)"
                    >
                      <CIcon name="cipDownload" size="sm"/>
                      <translate translate-context="payout">Excel</translate>
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </CCardBody>
          </CCard>
          
          <!-- Action Buttons -->
          <CRow class="mt-4" v-if="payout">
            <CCol lg="12">
              <div class="d-flex justify-content-end gap-2">
                <CButton
                  v-if="hasAnyPerms(['payout.create_payout']) && (payout.status === 'pending' || payout.status === 'requested')"
                  :color="payout.status === 'requested' ? 'warning' : 'primary'"
                  @click="openPayoutModal"
                >
                  <CIcon :name="payout.status === 'requested' ? 'cipUpload' : 'cipCash'" size="sm" class="mr-2"/>
                  <translate translate-context="payout">{{ payout.status === 'pending' ? 'Request Payment' : 'Upload Invoice' }}</translate>
                </CButton>
                <CButton
                  v-if="hasAnyPerms(['payout.delete_requested']) && payout.is_last_unpaid"
                  color="danger"
                  variant="outline"
                  @click="handleDeletePayout"
                >
                  <CIcon name="cipTrashFull" size="sm" class="mr-2"/>
                  <translate translate-context="payout">Delete Payout</translate>
                </CButton>
              </div>
            </CCol>
          </CRow>
        </CCol>
      </CRow>
    </PTransparentLoader>
    
    <!-- Modal richiesta payout -->
    <PayoutRequestModal
      v-if="hasAnyPerms(['payout.create_payout'])"
      ref="payoutModal"
      :contract="contract"
      :statistics="{}"
      @success="onPayoutRequested"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import PayoutRequestModal from './components/PayoutRequestModal.vue'
import { PAYOUT_STATUS, PAYOUT_STATUS_LABELS, PAYOUT_STATUS_COLORS } from './constants'

export default {
  name: 'PayoutDetail',
  components: {
    PTransparentLoader,
    PayoutRequestModal
  },
  data() {
    return {
      loading: true,
      serviceFields: [
        { 
          key: 'pica_service_name', 
          label: this.$pgettext('payout', 'Service Name'),
          _style: 'width: 30%'
        },
        { 
          key: 'photos_sold', 
          label: this.$pgettext('payout', 'Photos Sold'),
          _style: 'width: 15%',
          _classes: 'text-center'
        },
        { 
          key: 'paid_net', 
          label: this.$pgettext('payout', 'Net Amount'),
          _style: 'width: 20%',
          _classes: 'text-right'
        },
        { 
          key: 'last_order_paid_at', 
          label: this.$pgettext('payout', 'Last Order'),
          _style: 'width: 20%',
          _classes: 'text-center'
        },
        { 
          key: 'csv_file', 
          label: this.$pgettext('payout', 'Download'),
          _style: 'width: 15%',
          _classes: 'text-center'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('payout', ['payout']),
    ...mapGetters('events', ['contract']),
    contractId() {
      return parseInt(this.$route.params.contractId)
    },
    payoutId() {
      return parseInt(this.$route.params.payoutId)
    }
  },
  methods: {
    ...mapActions('payout', [
      'fetchPayoutDetail', 
      'deletePayout',
      'setContract',
      'downloadExcel',
      'downloadServiceExcel'
    ]),
    ...mapActions('events', ['setActiveContract']),
    
    async handleDownloadServiceCsv(serviceId) {
      try {
        await this.downloadServiceExcel({ payoutId: this.payoutId, serviceId })
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Error downloading Excel'))
      }
    },
    
    async handleDownloadConsolidatedCsv() {
      try {
        await this.downloadExcel(this.payoutId)
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Error downloading consolidated Excel'))
      }
    },
    
    getStatusLabel(status) {
      return PAYOUT_STATUS_LABELS[status] || status
    },
    
    getStatusColor(status) {
      return PAYOUT_STATUS_COLORS[status] || 'light'
    },
    
    getStatusIcon(status) {
      return status === PAYOUT_STATUS.PAYED ? 'cipDotGreen' : 'cipDotOrange'
    },
    
    openPayoutModal() {
      // Open modal to request/update payment with invoice and IBAN
      if (this.$refs.payoutModal && this.payout) {
        this.$refs.payoutModal.openWithPreparedPayout(this.payout)
      }
    },
    
    async onPayoutRequested() {
      // Reload payout detail after successful request
      await this.fetchPayoutDetail(this.payoutId)
      this.notifySuccess(this.$pgettext('payout', 'Payout request sent successfully'))
    },
    
    async handleDeletePayout() {
      if (!confirm(this.$pgettext('payout', 'Are you sure you want to delete this payout?'))) {
        return
      }
      
      try {
        await this.deletePayout(this.payoutId)
        this.notifySuccess(this.$pgettext('payout', 'Payout deleted successfully'))
        this.$router.push({ name: 'payoutList', params: { contractId: this.contractId } })
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Error deleting payout. Only the last pending payout can be deleted.'))
      }
    },
    
    async loadData() {
      this.loading = true
      
      try {
        // Set contract ID from route
        if (!this.contractId) {
          this.$router.push({ name: 'eventList' })
          return
        }
        
        // Load contract data and permissions FIRST
        await this.setActiveContract(this.contractId)
        
        // Set contract in payout store AFTER contract is loaded
        await this.setContract(this.contractId)
        
        // Now fetch payout detail
        await this.fetchPayoutDetail(this.payoutId)
        
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Error loading payout details'))
        this.$router.push({ name: 'payoutList', params: { contractId: this.contractId } })
      } finally {
        this.loading = false
      }
    }
  },
  async mounted() {
    await this.loadData()
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 1rem;
}
</style>